import React from 'react';
import {useNavigate} from "react-router-dom";
import {useDish} from "../../utils/contexts/dish-context";
import Button from "../UI/Button";
import Loading from "../UI/Loading";
import {useTranslation} from "react-i18next";
import TabelogInput from "./TabelogInput";
import {useApi} from "../../utils/hooks/api";
import {doGetAllergens, doGetDiets, doPostTestDish} from "../../logic/dish";
import {IoChevronDown, IoChevronUp} from "react-icons/io5";
import {FaRegCheckCircle, FaRegTimesCircle} from "react-icons/fa";

const IMAGE_ENDPOINT = process.env.PUBLIC_URL + "/assets/";

const TabelogDataDisplay = () => {
    const navigate = useNavigate();
    const {tabelogData, setTabelogData} = useDish();
    const {t, i18n} = useTranslation();
    const {call, loading} = useApi();
    const [dishIngredients, setDishIngredients] = React.useState({});
    const [dishAllergens, setDishAllergens] = React.useState({});
    const [dishDiets, setDishDiets] = React.useState({});
    const [expandedDishes, setExpandedDishes] = React.useState({});
    const [loadingDishId, setLoadingDishId] = React.useState(null);

    const isJapanese = i18n.language === 'ja';
    React.useEffect(() => {
        if (!isJapanese) {
            navigate('/impact/calculate');
        }
    }, [isJapanese, navigate]);

    const restaurant = tabelogData?.restaurant || (Array.isArray(tabelogData) ? tabelogData[0] : null);

    const handleBack = () => {
        setTabelogData(null);
        navigate('/tabelog');
    };

    const handleCalculateImpact = async (d, courseId) => {
        if (dishIngredients[d.id]) {
            return;
        }

        setLoadingDishId(d.id);
        const description = d.description || d.pia_descrizione || '';
        try {
            await call(
                () => doPostTestDish(d.name, 'ja', description, restaurant.id, courseId),
                async (res) => {
                    const ingredients = (res && res.data && res.data.ingredients) || (res && res.ingredients);
                    const dishId = (res && res.data && res.data.id) || (res && res.id);

                    if (ingredients) {
                        const ingredientsArray = Array.isArray(ingredients) ? ingredients : (ingredients.ingredients || []);
                        setDishIngredients(prev => ({
                            ...prev,
                            [d.id]: ingredientsArray
                        }));

                        if (dishId) {
                            try {
                                const allergensResponse = await doGetAllergens(dishId);
                                if (allergensResponse && allergensResponse.response) {
                                    setDishAllergens(prev => ({
                                        ...prev,
                                        [d.id]: allergensResponse.response
                                    }));
                                }

                                const dietsResponse = await doGetDiets(dishId);
                                if (dietsResponse && dietsResponse.response) {
                                    setDishDiets(prev => ({
                                        ...prev,
                                        [d.id]: dietsResponse.response
                                    }));
                                }
                            } catch (e) {
                                console.error("Error fetching allergens or diets:", e);
                            }
                        }

                        setExpandedDishes(prev => ({
                            ...prev,
                            [d.id]: true
                        }));
                    }
                }
            );
        } finally {
            setLoadingDishId(null);
        }
    };

    const handleImpact = () => {
        navigate('/impact/calculate');
    }

    const handleGdo = () => {
        navigate('/gdo');
    }

    const toggleIngredients = (dishId) => {
        setExpandedDishes(prev => ({
            ...prev,
            [dishId]: !prev[dishId]
        }));
    };

    if (!isJapanese) return null;

    return (
        <div className="min-h-screen">

            <div className="w-full">
                <div className="p-2 bg-blue-100 relative">
                    <div className="absolute top-0 right-0 flex gap-2 items-center justify-center px-2 py-3.5">
                        <Button onClick={handleImpact}>IMPACT</Button>
                        <Button onClick={handleGdo}>GDO</Button>
                    </div>
                    <div className="text-lg font-bold uppercase mb-2">
                        {t('tabelog.title', 'Calculate Tabelog')}
                    </div>
                    <TabelogInput />
                </div>

                {restaurant ? (
                    <>
                        <div className="p-4 flex justify-between items-center mb-6">
                            <h1 className="text-2xl font-bold uppercase text-blue-800">{restaurant.name}</h1>
                        </div>

                        {restaurant.courses && restaurant.courses.length > 0 ? (
                            <div className="p-4 grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
                                {restaurant.courses.map((course) => (
                                    <div key={course.id} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
                                        <div className="bg-blue-100 px-4 py-3 border-b border-blue-200">
                                            <h2 className="text-xl font-semibold text-blue-900 line-clamp-2">{course.name}</h2>
                                        </div>
                                        <div className="p-4 flex-grow">
                                            {course.dishes && course.dishes.length > 0 ? (
                                                <div className="grid grid-cols-2 gap-4">
                                                    {course.dishes.map((dish) => (
                                                        <div key={dish.id} className="border rounded p-3 hover:bg-gray-50 transition-colors flex flex-col gap-2">
                                                            <h3 className="font-bold text-lg text-gray-800">{dish.name}</h3>
                                                            {dish.description && (
                                                                <p className="text-gray-600 text-sm mt-1 whitespace-pre-wrap">{dish.description}</p>
                                                            )}
                                                            <div className="mt-auto pt-2">
                                                                <Button onClick={() => handleCalculateImpact(dish, course.id)} disabled={loading}>
                                                                    {loading && loadingDishId === dish.id ? <Loading label={t('loading', 'Loading...')} /> : t('tabelog.ingredients')}
                                                                </Button>
                                                            </div>

                                                            {dishIngredients[dish.id] && (
                                                                <div className="mt-2 bg-gray-100 rounded text-sm overflow-hidden animate-in fade-in duration-300 border border-gray-200">
                                                                    <button
                                                                        onClick={() => toggleIngredients(dish.id)}
                                                                        className="w-full px-2 py-1 flex justify-between items-center bg-gray-200 hover:bg-gray-300 transition-colors font-semibold text-gray-700"
                                                                    >
                                                                        <span>{t('tabelog.ingredients')}</span>
                                                                        {expandedDishes[dish.id] ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
                                                                    </button>
                                                                    {expandedDishes[dish.id] && (
                                                                        <div className="p-2">
                                                                            <ul className="list-disc list-inside pr-2 mb-4">
                                                                                {dishIngredients[dish.id].map((ingredient, idx) => (
                                                                                    <li key={ingredient.id || idx}>
                                                                                        {ingredient.name} {ingredient.it_name ? `(${ingredient.it_name})` : ''}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>

                                                                            {dishAllergens[dish.id] && dishAllergens[dish.id].length > 0 && (
                                                                                <div className="mt-4 pt-2 border-t border-gray-200">
                                                                                    <div className="font-bold text-gray-700 mb-2 uppercase text-xs">{t('calculate_impact.allergens', 'Allergens')}</div>
                                                                                    <div className="flex flex-wrap gap-3">
                                                                                        {dishAllergens[dish.id].map((allergen) => (
                                                                                            <div key={allergen.id_dieta} className="flex items-center gap-1 bg-white px-2 py-1 rounded border border-gray-200 shadow-sm">
                                                                                                <img
                                                                                                    className="w-5 h-5 object-contain"
                                                                                                    src={IMAGE_ENDPOINT + allergen.allergia_icona}
                                                                                                    alt={allergen.allergia_nome}
                                                                                                />
                                                                                                <span className="text-xs">{allergen.allergia_nome}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {dishDiets[dish.id] && dishDiets[dish.id].length > 0 && (
                                                                                <div className="mt-4 pt-2 border-t border-gray-200">
                                                                                    <div className="font-bold text-gray-700 mb-2 uppercase text-xs">{t('calculate_impact.food_regimes', 'Diets')}</div>
                                                                                    <div className="grid grid-cols-2 gap-2">
                                                                                        {dishDiets[dish.id].map((diet) => (
                                                                                            <div key={diet.id_dieta} className="flex items-center gap-1.5 px-2 py-1 rounded bg-white border border-gray-200 shadow-sm">
                                                                                                {diet.compatibile ?
                                                                                                    <FaRegCheckCircle className="text-green-500 shrink-0" size={14}/>
                                                                                                    : <FaRegTimesCircle className="text-red-500 shrink-0" size={14}/>
                                                                                                }
                                                                                                <span className={`text-xs font-medium ${diet.compatibile ? "text-green-600" : "text-red-600"}`}>
                                                                                                    {t(`diets.${diet.dieta}`)}
                                                                                                </span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-gray-500 italic">{t('tabelog.no_dishes_available', 'Nessun piatto disponibile per questa sezione.')}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-4">
                                <div className="bg-white p-8 rounded-lg shadow text-center">
                                    <p className="text-gray-600">{t('tabelog.no_menu_found', 'Nessun menu trovato per questo ristorante.')}</p>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="p-4">
                        <div className="bg-white p-8 rounded-lg shadow text-center">
                            <p className="text-gray-600">{t('tabelog.no_data_found', 'Nessun dato Tabelog trovato.')}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TabelogDataDisplay;
